import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Chip, GalleryDiorama } from '@/design-system'
import { SCENE_PRESET_GEOMETRY } from '@/features/gallery-studio-3d/constants'
import { getClassicFamilyForId } from '@/features/gallery-studio-3d/classicCatalog'
import { GallerySummary } from '../../types'
import { formatRelative, formatMonthYear } from '../../utils/date'
import { CLASSIC_ROOM_TINT, isLightColor } from '../../utils/roomColor'
import { CardOwnerBadge } from '../CardOwnerBadge'
import { ReportMenu } from '../ReportMenu'

export interface GalleryCardProps {
  gallery: GallerySummary
  onClick?: () => void
  /** Source album's title, resolved client-side from the shell's already-loaded album list. */
  sourceAlbumTitle?: string
  /** Source album's cover photo — shown as a single hung print until the real scene note loads. */
  sourceAlbumCoverUrl?: string
  index?: number
  /** Shows a report kebab on the cover — Discover-only; unset on "my stuff" grids. */
  onReport?: () => void
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  animation: `${fadeUp} 480ms var(--ds-ease) both`,
  transition: [
    `transform var(--ds-duration-normal) var(--ds-ease-out, var(--ds-ease))`,
    `box-shadow var(--ds-duration-normal) var(--ds-ease)`,
  ].join(', '),
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 'var(--ds-shadow-lift)',
  },
  '&:hover .gallery-card-edge': { opacity: 1 },
  '&:hover .card-kebab': { opacity: 1 },
})

const SpectrumEdge = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 2.5,
  zIndex: 3,
  background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
  opacity: 0,
  transition: `opacity var(--ds-duration-normal) var(--ds-ease)`,
})

const Cov = styled(Box)({
  position: 'relative',
  aspectRatio: '4 / 3.05',
  overflow: 'hidden',
  backgroundColor: 'var(--ds-surface)',
})

const Veil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, transparent 62%, rgba(0,0,0,0.3))',
  pointerEvents: 'none',
  zIndex: 2,
})

const badgeSx = {
  position: 'absolute' as const,
  top: 10,
  left: 10,
  height: 24,
  background: 'rgba(13,13,15,0.65)',
  backdropFilter: 'blur(8px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  zIndex: 3,
}

const privacyChipSx = { ...badgeSx, left: 'auto', right: 10 }

const BuildingChip = styled(Box)({
  position: 'absolute',
  top: 10,
  right: 10,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 10px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(255,207,125,0.94)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: '#4a3510',
  fontWeight: 700,
  zIndex: 3,
})

const Meta = styled(Box)({
  padding: '14px 16px 15px',
})

const Name = styled(Typography)({
  fontSize: 15,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-ink)',
})

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 5,
})

const Signal = styled(Typography)({
  fontSize: 11.5,
  color: 'var(--ds-ink-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

const Stats = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.08em',
  color: 'var(--ds-ink-subtle)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export function GalleryCard({
  gallery,
  onClick,
  sourceAlbumTitle,
  sourceAlbumCoverUrl,
  index = 0,
  onReport,
}: GalleryCardProps) {
  const navigate = useNavigate()
  const isPrivate = gallery.privacy === 'private'
  const relative = formatRelative(gallery.updatedAt)
  const monthYear = formatMonthYear(gallery.updatedAt)
  const isClassic = gallery.mode === 'classic'

  // Real room colors once the scene note loads. `studio` falls back to this
  // room's actual default (ROOM_DEFAULTS is `corridor`) — not a guess.
  // `classic` rooms are fixed meshes with no configurable tint, so there's
  // no real fallback — use a neutral stand-in distinct from every studio
  // preset (see roomColor.ts) until the note resolves.
  const geo = SCENE_PRESET_GEOMETRY[gallery.scenePreset || 'corridor']
  const fallbackTint = isClassic ? CLASSIC_ROOM_TINT : geo
  const wallColor = gallery.wallColor || fallbackTint.wallColor
  const floorColor = gallery.floorColor || fallbackTint.floorColor

  // Real hung prints once loaded (the scene note's `arts[]` covers classic
  // rooms too — only the frame mesh differs, not the array); else the source
  // album's real cover as a single stand-in print; else empty (ghost frames).
  const arts =
    gallery.arts.length > 0
      ? gallery.arts
      : sourceAlbumCoverUrl
        ? [{ img: sourceAlbumCoverUrl }]
        : []
  const stillBuilding = gallery.sceneLoaded && gallery.arts.length === 0
  const classicLabel = isClassic
    ? getClassicFamilyForId(gallery.classicTemplateId ?? -1)?.label || 'Classic'
    : null
  const isLight = isLightColor(wallColor)

  const onSourceAlbumClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/albums/${gallery.sourceAlbumId}`)
  }

  return (
    <Root
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
    >
      <SpectrumEdge className='gallery-card-edge' />
      <Cov>
        <GalleryDiorama wallColor={wallColor} floorColor={floorColor} arts={arts} light={isLight} />
        <Veil />
        <Chip tone='live' sx={{ ...badgeSx, color: isClassic ? '#01dafe' : '#ffcf7d' }}>
          {isClassic ? `◇ ${classicLabel!.toUpperCase()}` : `◈ ${geo.label.toUpperCase()}`}
        </Chip>
        {isPrivate ? (
          <Chip tone='live' sx={privacyChipSx}>
            <LockOutlinedIcon sx={{ fontSize: 10 }} /> PRIVATE
          </Chip>
        ) : stillBuilding ? (
          <BuildingChip>STILL HANGING PRINTS</BuildingChip>
        ) : null}
        {gallery.owner && <CardOwnerBadge owner={gallery.owner} likes={gallery.likes} />}
        {onReport && <ReportMenu itemLabel={gallery.title} onReport={onReport} />}
      </Cov>
      <Meta>
        <Name noWrap>{gallery.title}</Name>
        <MetaRow>
          <Signal>
            {sourceAlbumTitle ? (
              <span onClick={onSourceAlbumClick} style={{ cursor: 'pointer' }}>
                From <b>{sourceAlbumTitle}</b> ↗
              </span>
            ) : relative ? (
              `Updated ${relative}`
            ) : (
              '\u00A0'
            )}
          </Signal>
          <Stats>
            {gallery.printCount} {monthYear ? `· ${monthYear}` : ''}
          </Stats>
        </MetaRow>
      </Meta>
    </Root>
  )
}
